import { RPACourse } from '../types';

export const UIPATH_COURSES: RPACourse[] = [
  {
    id: 'uipath-fundamentals',
    title: 'UiPath Fundamentals & Studio Basics',
    description: 'Master the fundamentals of UiPath Studio, learn to create your first automation workflows, and understand RPA core concepts.',
    level: 'beginner',
    duration: '12 hours',
    category: 'uipath',
    platform: 'UiPath Studio',
    certificationPrep: true,
    prerequisites: [],
    learningObjectives: [
      'Navigate UiPath Studio interface and create basic workflows',
      'Understand activities, variables, and arguments in UiPath',
      'Build your first automation bot from scratch',
      'Handle selectors and UI automation',
      'Implement basic error handling and debugging'
    ],
    tags: ['UiPath', 'RPA Basics', 'Studio', 'Automation', 'Workflows'],
    modules: [
      {
        id: 'uipath-intro',
        title: 'Introduction to UiPath and RPA',
        description: 'Learn RPA fundamentals and UiPath ecosystem overview',
        content: `# Introduction to UiPath and RPA

## What is Robotic Process Automation (RPA)?

Robotic Process Automation (RPA) is a technology that uses software robots (bots) to automate repetitive, rule-based business processes. These digital workers can interact with applications, process data, and perform tasks just like human users, but with greater speed, accuracy, and consistency.

## Key Benefits of RPA

- **Cost Reduction**: Reduce operational costs by up to 60% through automation
- **Improved Accuracy**: Eliminate human errors in repetitive tasks
- **24/7 Availability**: Bots work around the clock without breaks
- **Scalability**: Easily scale automation based on business needs
- **Compliance**: Maintain consistent adherence to business rules and regulations

## UiPath Platform Overview

UiPath is a leading RPA platform that provides a comprehensive suite of tools for building, deploying, and managing automation solutions:

### UiPath Studio
The development environment where you design and build automation workflows using a visual, drag-and-drop interface.

### UiPath Orchestrator
The central management platform for deploying, scheduling, monitoring, and managing your automation robots.

### UiPath Robot
The execution engine that runs your automation workflows, available in attended and unattended modes.

### UiPath AI Center
Advanced platform for integrating machine learning models and AI capabilities into your automations.

## Common RPA Use Cases

### Financial Services
- Invoice processing and accounts payable automation
- Account reconciliation and reporting
- Loan processing and customer onboarding
- Regulatory compliance reporting

### Healthcare
- Patient registration and data entry
- Insurance claim processing
- Medical record management
- Appointment scheduling

### Manufacturing
- Supply chain management
- Inventory tracking and reporting
- Quality control data collection
- Order processing automation

## Getting Started with UiPath

### Prerequisites
- Windows operating system (Windows 10 or later recommended)
- .NET Framework 4.6.1 or later
- Basic understanding of business processes
- Logical thinking and problem-solving skills

### Installation Steps
1. Download UiPath Studio Community Edition (free for personal/educational use)
2. Create a UiPath account and obtain a license
3. Install UiPath Studio following the installation wizard
4. Complete the initial setup and connect to UiPath Orchestrator (if required)

## Best Practices for RPA Implementation

### Process Selection Criteria
- **High Volume**: Processes with large transaction volumes
- **Rule-Based**: Clear, well-defined business rules
- **Stable**: Processes that don't change frequently
- **Digital**: Processes that involve digital systems and data

### Development Guidelines
- Start with simple processes and gradually increase complexity
- Follow naming conventions for activities and variables
- Implement proper error handling and logging
- Create reusable components and libraries
- Document your automation workflows thoroughly

## Key Takeaways

Understanding RPA and the UiPath platform is the foundation for successful automation implementation. Focus on identifying the right processes to automate, understanding the business value, and following best practices from the beginning. UiPath's comprehensive platform provides all the tools needed to build robust, scalable automation solutions that can transform business operations.`,
        duration: '2 hours',
        difficulty: 'beginner',
        practiceExercises: [
          'Install UiPath Studio Community Edition',
          'Explore UiPath Academy and Community resources',
          'Identify automation opportunities in daily tasks'
        ]
      },
      {
        id: 'studio-basics',
        title: 'UiPath Studio Interface and Basic Components',
        description: 'Master the Studio interface, activities panel, and project structure',
        content: `# UiPath Studio Interface and Basic Components

## Understanding the UiPath Studio Interface

UiPath Studio is the primary development environment for creating automation workflows. Mastering its interface is crucial for efficient automation development.

### Main Interface Components

#### Activities Panel
The Activities panel contains all available automation actions, organized into categories:
- **System**: Basic system operations (click, type, delays)
- **UI Automation**: User interface interaction activities
- **App Integration**: Integration with specific applications (Excel, Email, etc.)
- **Programming**: Control flow, variables, and data manipulation
- **AI**: Machine learning and artificial intelligence activities

#### Design Panel
The central workspace where you build your automation workflows by dragging and dropping activities.

#### Properties Panel
Displays and allows modification of activity properties, including input/output parameters and configuration options.

#### Output Panel
Shows execution results, error messages, and debugging information during workflow development and testing.

## Project Types in UiPath Studio

### Process
The standard project type for creating automation workflows. Suitable for most business process automation scenarios.

### Library
Used for creating reusable components that can be shared across multiple projects. Ideal for common functions and utilities.

### Test
Specifically designed for creating test automation workflows to validate other automation processes.

### Template
Pre-configured project structures that provide starting points for common automation scenarios.

## Understanding Activities

Activities are the building blocks of UiPath automations. Each activity performs a specific action or operation.

### Basic Activity Categories

#### Input Activities
- **Type Into**: Simulates typing text into applications
- **Click**: Simulates mouse clicks on UI elements
- **Send Hotkey**: Sends keyboard shortcuts to applications

#### Output Activities
- **Get Text**: Extracts text from UI elements
- **Get Attribute**: Retrieves specific attributes from UI elements
- **Take Screenshot**: Captures screen images for documentation or validation

#### Control Flow Activities
- **If**: Conditional logic for decision making
- **For Each**: Iterates through collections of items
- **While**: Repeats actions while a condition is true
- **Try Catch**: Error handling and exception management

### Activity Properties Configuration

Each activity has specific properties that control its behavior:

#### Common Properties
- **DisplayName**: Descriptive name for the activity
- **Timeout**: Maximum time to wait for the activity to complete
- **WaitForReady**: Determines when the activity should proceed
- **DelayBefore/DelayAfter**: Adds delays before or after execution

#### Selector Properties
Activities that interact with UI elements use selectors to identify target elements:
- **Selector**: XML-based element identification
- **Target**: Modern selector approach using UiPath Object Repository
- **Image**: Image-based element identification

## Workflow Design Principles

### Sequence Workflows
Linear workflows that execute activities in order. Best for straightforward, step-by-step processes.

### Flowchart Workflows
Visual workflows with decision points and multiple paths. Ideal for complex business logic with multiple conditions.

### State Machine Workflows
Event-driven workflows that respond to different states and triggers. Perfect for dynamic processes with multiple states.

## Best Practices for Interface Usage

### Organization and Navigation
- Use meaningful names for activities and variables
- Group related activities using containers
- Add annotations to explain complex logic
- Utilize the outline panel for easy navigation

### Efficiency Tips
- Use keyboard shortcuts for common operations (Ctrl+T for Type Into, Ctrl+K for Click)
- Customize the Activities panel by favoriting frequently used activities
- Use the search function to quickly find specific activities
- Take advantage of IntelliSense for property configuration

### Debugging and Testing
- Use breakpoints to pause execution and inspect workflow state
- Utilize the Step Into and Step Over debugging options
- Monitor variable values during execution
- Use the Highlight Elements feature to verify selector accuracy

## Working with the Project Panel

### File Organization
- **Main.xaml**: The entry point of your automation
- **Imports**: Manage namespace references for activities
- **Dependencies**: External packages and libraries used in the project
- **Data**: Folder for storing data files and resources

### Version Control Integration
UiPath Studio integrates with Git and other version control systems:
- Initialize Git repository directly from Studio
- Commit changes with meaningful messages
- Manage branches for collaborative development
- Resolve merge conflicts within the interface

## Advanced Interface Features

### Modern Design Experience
UiPath Studio offers a modern design experience with:
- **Object Repository**: Centralized UI element management
- **Modern Activities**: Enhanced activity set with improved performance
- **Unified Target**: Simplified element targeting approach

### Package Management
- Install packages from UiPath Go! marketplace
- Manage package versions and dependencies
- Create and publish custom packages

## Key Takeaways

Mastering the UiPath Studio interface is essential for productive automation development. Focus on understanding the main panels, activity organization, and workflow design principles. Practice using keyboard shortcuts and debugging tools to improve development efficiency. The modern design experience and advanced features provide powerful capabilities for building robust automation solutions.`,
        duration: '3 hours',
        difficulty: 'beginner',
        practiceExercises: [
          'Create a new blank project',
          'Navigate activities panel and add basic activities',
          'Design your first sequence workflow'
        ]
      },
      {
        id: 'variables-arguments',
        title: 'Variables, Arguments, and Data Manipulation',
        description: 'Learn to work with different data types and pass data between workflows',
        content: `# Variables, Arguments, and Data Manipulation

## Understanding Variables in UiPath

Variables are containers that store data values during workflow execution. They are essential for data manipulation, temporary storage, and passing information between activities.

### Variable Types

#### Basic Data Types
- **String**: Text values (e.g., "Hello World", customer names, addresses)
- **Int32**: Whole numbers (e.g., 42, -15, 0)
- **Double**: Decimal numbers (e.g., 3.14, -2.5, 100.99)
- **Boolean**: True/false values for logical operations
- **DateTime**: Date and time values for temporal operations

#### Advanced Data Types
- **DataTable**: Tabular data structure for handling Excel-like data
- **Array**: Collection of items of the same type
- **List**: Dynamic collection that can grow or shrink
- **Dictionary**: Key-value pairs for efficient data lookup

### Variable Creation and Management

#### Creating Variables
1. **Variables Panel**: Use the Variables panel at the bottom of Studio
2. **Ctrl+K**: Quick variable creation during activity configuration
3. **Property Grid**: Create variables directly in activity properties

#### Variable Naming Conventions
- Use meaningful, descriptive names (e.g., \`customerName\` instead of \`var1\`)
- Follow camelCase naming convention
- Include data type hints when helpful (e.g., \`customerListDT\` for DataTable)
- Avoid special characters and spaces

#### Variable Scope
- **Sequence Scope**: Available only within the current sequence
- **Flowchart Scope**: Available throughout the entire flowchart
- **Global Scope**: Available across all workflows in the project

## Working with Arguments

Arguments enable data exchange between different workflows, making automations modular and reusable.

### Argument Types

#### Direction Types
- **In**: Receives data from the calling workflow
- **Out**: Sends data back to the calling workflow
- **In/Out**: Both receives and sends data
- **Property**: Special type for library activities

#### Argument Data Types
Arguments can use any of the same data types as variables, including:
- Primitive types (String, Int32, Boolean, etc.)
- Complex types (DataTable, Array, Dictionary, etc.)
- Custom object types

### Best Practices for Arguments

#### Naming and Documentation
- Use descriptive names that indicate purpose and direction
- Add detailed descriptions explaining the argument's role
- Include data format specifications and constraints
- Document expected value ranges or formats

#### Validation and Error Handling
- Validate input arguments at workflow entry points
- Provide meaningful error messages for invalid arguments
- Use default values for optional arguments
- Implement null checking for reference types

## Data Manipulation Techniques

### String Operations

#### Common String Methods
\`\`\`vb
' String concatenation
fullName = firstName + " " + lastName

' String formatting
message = String.Format("Hello {0}, today is {1}", name, DateTime.Now.ToString("dddd"))

' String manipulation
upperName = customerName.ToUpper()
cleanedText = rawText.Trim()
nameLength = customerName.Length
\`\`\`

#### String Parsing and Validation
\`\`\`vb
' Checking if string contains specific text
If customerEmail.Contains("@") Then
    ' Valid email format
End If

' Splitting strings
nameParts = fullName.Split(" "c)
firstName = nameParts(0)
lastName = nameParts(1)

' String replacement
cleanedData = rawData.Replace(",", ";")
\`\`\`

### Numeric Operations

#### Mathematical Calculations
\`\`\`vb
' Basic arithmetic
total = price * quantity
discount = total * 0.1
finalAmount = total - discount

' Rounding and formatting
roundedAmount = Math.Round(finalAmount, 2)
formattedPrice = finalAmount.ToString("C") ' Currency format
\`\`\`

#### Type Conversions
\`\`\`vb
' String to number conversion
quantity = Convert.ToInt32(quantityText)
price = Convert.ToDouble(priceText)

' Number to string conversion
quantityText = quantity.ToString()
priceText = price.ToString("F2") ' Two decimal places
\`\`\`

### DateTime Operations

#### Date Manipulation
\`\`\`vb
' Current date and time
currentDateTime = DateTime.Now
currentDate = DateTime.Today

' Date arithmetic
futureDate = currentDate.AddDays(30)
pastDate = currentDate.AddMonths(-6)
timeSpan = futureDate.Subtract(currentDate)

' Date formatting
formattedDate = currentDate.ToString("yyyy-MM-dd")
displayDate = currentDate.ToString("MMMM d, yyyy")
\`\`\`

### Collection Operations

#### Working with Arrays
\`\`\`vb
' Array creation and initialization
fruits = {"Apple", "Banana", "Orange"}
numbers = {1, 2, 3, 4, 5}

' Array operations
firstFruit = fruits(0)
arrayLength = fruits.Length
\`\`\`

#### Working with Lists
\`\`\`vb
' List creation and manipulation
customerList = New List(Of String)
customerList.Add("John Doe")
customerList.Add("Jane Smith")

' List operations
customerCount = customerList.Count
firstCustomer = customerList(0)
customerList.Remove("John Doe")
\`\`\`

#### Working with Dictionaries
\`\`\`vb
' Dictionary creation and usage
customerData = New Dictionary(Of String, String)
customerData("Name") = "John Doe"
customerData("Email") = "john@example.com"

' Dictionary operations
customerName = customerData("Name")
hasEmail = customerData.ContainsKey("Email")
\`\`\`

## Advanced Data Manipulation

### LINQ Operations
Language Integrated Query (LINQ) provides powerful data manipulation capabilities:

\`\`\`vb
' Filtering data
expensiveItems = products.Where(Function(p) p.Price > 100).ToList()

' Selecting specific properties
customerNames = customers.Select(Function(c) c.Name).ToList()

' Grouping data
groupedOrders = orders.GroupBy(Function(o) o.CustomerID).ToList()
\`\`\`

### Regular Expressions
Use regular expressions for complex string pattern matching:

\`\`\`vb
' Email validation pattern
emailPattern = "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
isValidEmail = System.Text.RegularExpressions.Regex.IsMatch(email, emailPattern)

' Extract phone numbers
phonePattern = "\d{3}-\d{3}-\d{4}"
phoneMatches = System.Text.RegularExpressions.Regex.Matches(text, phonePattern)
\`\`\`

## Error Handling and Validation

### Input Validation
Always validate data before processing:

\`\`\`vb
' Null checking
If Not String.IsNullOrEmpty(customerName) Then
    ' Process customer name
End If

' Numeric validation
If IsNumeric(priceText) Then
    price = Convert.ToDouble(priceText)
Else
    ' Handle invalid numeric input
End If
\`\`\`

### Exception Handling
Use Try-Catch activities for robust error handling:

\`\`\`vb
Try
    ' Risky operations
    result = Convert.ToInt32(userInput)
Catch ex As FormatException
    ' Handle format conversion errors
    result = 0
Catch ex As Exception
    ' Handle other exceptions
    logMessage = "Unexpected error: " + ex.Message
End Try
\`\`\`

## Key Takeaways

Mastering variables, arguments, and data manipulation is crucial for building robust UiPath automations. Focus on proper naming conventions, understanding data types, and implementing appropriate validation and error handling. Practice with different data manipulation techniques and always consider the scope and lifecycle of your variables. Effective data management makes your automations more reliable, maintainable, and scalable.`,
        duration: '2.5 hours',
        difficulty: 'beginner',
        practiceExercises: [
          'Create and use different variable types',
          'Pass arguments between workflows',
          'Manipulate strings and perform calculations'
        ]
      },
      {
        id: 'ui-automation',
        title: 'UI Automation and Selectors',
        description: 'Master web and desktop application automation with reliable selectors',
        content: `# UI Automation and Selectors

## Understanding UI Automation

User Interface (UI) automation is the core capability that enables RPA bots to interact with applications just like human users. This involves clicking buttons, typing text, extracting data, and navigating through application interfaces.

## Types of Application Automation

### Web Applications
Modern web applications present unique challenges and opportunities:

#### Browser Support
- **Chrome**: Excellent support with UiPath Web Extension
- **Firefox**: Good support with native UiPath integration
- **Edge**: Full support with built-in automation capabilities
- **Internet Explorer**: Legacy support (not recommended for new projects)

#### Web Automation Techniques
- **Element-based**: Direct interaction with HTML elements
- **Image-based**: Visual element recognition and clicking
- **Coordinate-based**: Fixed position clicking (least reliable)

### Desktop Applications

#### Windows Applications
- **WinForms**: Excellent automation support with detailed element properties
- **WPF**: Good support with rich element information
- **Win32**: Basic support with limited element details
- **Java Applications**: Requires Java Bridge installation

#### Legacy System Automation
- **Mainframe/Terminal**: Screen scraping and coordinate-based automation
- **Citrix/Virtual Environments**: Image-based automation techniques
- **Older Windows Applications**: Mixed approach using multiple automation methods

## Understanding Selectors

Selectors are XML fragments that identify UI elements uniquely. They are the foundation of reliable UI automation.

### Selector Structure

A typical selector looks like this:
\`\`\`xml
<wnd app='notepad.exe' cls='Notepad' title='Untitled - Notepad' />
<wnd cls='Edit' />
\`\`\`

#### Selector Components
- **Tag Name**: Type of UI element (wnd, html, java, etc.)
- **Attributes**: Properties that identify the element
- **Hierarchy**: Parent-child relationship between elements

### Selector Attributes

#### Common Attributes
- **aaname**: Accessible name of the element
- **class**: CSS class or window class name
- **id**: Unique identifier of the element
- **name**: Name attribute of the element
- **tag**: HTML tag name
- **title**: Title or text content of the element

#### Advanced Attributes
- **tableRow/tableCol**: Position in tables
- **idx**: Index when multiple similar elements exist
- **parentclass**: Parent element's class
- **url**: Current page URL (for web elements)

### Building Reliable Selectors

#### Best Practices for Selector Creation

##### Use Stable Attributes
Prioritize attributes that don't change frequently:
\`\`\`xml
<!-- Good: Uses stable ID -->
<html app='chrome.exe' title='Customer Portal' />
<webctrl id='customer-search-button' tag='BUTTON' />

<!-- Poor: Uses dynamic text that might change -->
<html app='chrome.exe' title='Customer Portal' />
<webctrl aaname='Search for customers (245 found)' tag='BUTTON' />
\`\`\`

##### Create Dynamic Selectors
Use wildcards and variables for flexible element identification:
\`\`\`xml
<!-- Using wildcards -->
<html app='chrome.exe' title='*Customer Portal*' />
<webctrl id='customer-*' tag='BUTTON' />

<!-- Using variables -->
<html app='chrome.exe' title='{{BrowserTitle}}' />
<webctrl aaname='{{ButtonText}}' tag='BUTTON' />
\`\`\`

##### Optimize Selector Hierarchy
Remove unnecessary parent elements to improve performance:
\`\`\`xml
<!-- Over-specified (slower) -->
<html app='chrome.exe' title='Portal' />
<webctrl tag='DIV' class='container' />
<webctrl tag='DIV' class='form-group' />
<webctrl tag='INPUT' id='username' />

<!-- Optimized (faster) -->
<html app='chrome.exe' title='Portal' />
<webctrl tag='INPUT' id='username' />
\`\`\`

## Advanced UI Automation Techniques

### Dynamic Element Handling

#### Waiting Strategies
Implement proper waiting mechanisms for dynamic content:

\`\`\`vb
' Wait for element to appear
WaitForReady: Interactive
Timeout: 30000 (30 seconds)

' Check if element exists before interaction
If ElementExists(targetSelector) Then
    Click targetElement
Else
    Log "Element not found, using alternative approach"
End If
\`\`\`

#### Element State Validation
Verify element state before interaction:

\`\`\`vb
' Check if button is enabled
If GetAttribute(buttonSelector, "disabled") = "" Then
    Click buttonSelector
Else
    Log "Button is disabled, waiting for enable"
End If
\`\`\`

### Table and Grid Automation

#### Data Extraction from Tables
\`\`\`vb
' Extract entire table
dataTable = ExtractDataTable(tableSelector, ExtractMetadata)

' Extract specific cell
cellValue = GetText(cellSelector)

' Iterate through table rows
For Each row As DataRow In dataTable.Rows
    customerName = row("Customer Name").ToString()
    customerEmail = row("Email").ToString()
    ' Process each row
Next
\`\`\`

#### Table Navigation and Interaction
\`\`\`vb
' Find row by content
For rowIndex = 0 To dataTable.Rows.Count - 1
    If dataTable.Rows(rowIndex)("Customer Name").ToString() = targetCustomer Then
        ' Click on specific row
        rowSelector = "//tr[" + (rowIndex + 1).ToString() + "]"
        Click rowSelector
        Exit For
    End If
Next
\`\`\`

### Form Automation

#### Smart Form Filling
Create reusable form-filling routines:

\`\`\`vb
' Define form data structure
formData = New Dictionary(Of String, String) From {
    {"firstName", "John"},
    {"lastName", "Doe"},
    {"email", "john.doe@example.com"},
    {"phone", "555-123-4567"}
}

' Fill form dynamically
For Each field In formData
    fieldSelector = "<webctrl name='" + field.Key + "' />"
    TypeInto fieldSelector, field.Value
Next
\`\`\`

#### Form Validation and Error Handling
\`\`\`vb
' Validate required fields before submission
requiredFields = {"firstName", "lastName", "email"}

For Each field In requiredFields
    fieldSelector = "<webctrl name='" + field + "' />"
    fieldValue = GetText(fieldSelector)
    
    If String.IsNullOrEmpty(fieldValue) Then
        Log "Required field '" + field + "' is empty"
        ' Handle validation error
    End If
Next
\`\`\`

## Modern UI Automation Features

### Object Repository
Centralize UI element management:

1. **Create Object Repository**: Store commonly used selectors
2. **Maintain Element Versions**: Track selector changes over time
3. **Share Across Projects**: Reuse elements in multiple workflows
4. **Automatic Maintenance**: Self-healing capabilities for broken selectors

### Computer Vision
Leverage AI-powered element detection:

#### When to Use Computer Vision
- Applications with poor selector support
- Dynamic interfaces that change frequently
- Image-based validations and verifications
- Cross-platform automation scenarios

#### Best Practices for Computer Vision
- Use high-resolution screenshots for training
- Implement fuzzy matching for slight variations
- Combine with traditional selectors for reliability
- Test across different screen resolutions and scaling

### Mobile Automation
Extend automation to mobile applications:

#### Mobile Automation Capabilities
- Native mobile app automation
- Mobile web browser automation
- Cross-platform mobile testing
- Device simulation and emulation

## Troubleshooting Common Issues

### Selector Problems

#### Element Not Found
1. **Verify element existence**: Use Element Exists activity
2. **Check timing**: Implement appropriate wait strategies
3. **Update selectors**: Refresh selectors if application changed
4. **Alternative identification**: Try different selector attributes

#### Slow Performance
1. **Optimize selectors**: Remove unnecessary hierarchy levels
2. **Use specific attributes**: Prefer ID over generic attributes
3. **Implement caching**: Store frequently used elements
4. **Reduce wait times**: Optimize timeout values

#### Unstable Automation
1. **Dynamic content**: Handle loading states properly
2. **Multiple instances**: Use process-specific selectors
3. **Resolution changes**: Use relative positioning
4. **Browser extensions**: Consider extension interference

### Error Recovery Strategies

#### Implement Retry Logic
\`\`\`vb
retryCount = 0
maxRetries = 3

Do While retryCount < maxRetries
    Try
        Click targetButton
        Exit Do ' Success, exit loop
    Catch ex As Exception
        retryCount = retryCount + 1
        If retryCount < maxRetries Then
            Delay 2000 ' Wait 2 seconds before retry
        Else
            Throw ex ' Re-throw if all retries failed
        End If
    End Try
Loop
\`\`\`

#### Graceful Degradation
\`\`\`vb
' Try primary method first
Try
    Click primaryButtonSelector
Catch
    Try
        ' Fall back to alternative method
        Click alternativeButtonSelector
    Catch
        ' Final fallback using keyboard
        SendHotkey "tab tab enter"
    End Try
End Try
\`\`\`

## Key Takeaways

Mastering UI automation requires understanding different application types, creating reliable selectors, and implementing robust error handling. Focus on building dynamic, maintainable selectors that can adapt to application changes. Leverage modern features like Object Repository and Computer Vision to create more resilient automations. Always implement proper waiting strategies and error recovery mechanisms to ensure your automations work reliably in production environments.`,
        duration: '3 hours',
        difficulty: 'intermediate',
        practiceExercises: [
          'Automate web form filling',
          'Extract data from desktop applications',
          'Create dynamic selectors for robust automation'
        ]
      },
      {
        id: 'control-flow',
        title: 'Control Flow and Error Handling',
        description: 'Implement decision logic, loops, and comprehensive error handling',
        content: `# Control Flow and Error Handling

## Understanding Control Flow

Control flow determines the order and conditions under which activities execute in your automation workflows. Mastering control flow is essential for building intelligent, adaptive automations.

## Conditional Logic

### If Activity
The If activity enables decision-making in workflows based on Boolean conditions.

#### Basic If Structure
\`\`\`vb
' Simple condition checking
If customerAge >= 18 Then
    ' Adult customer processing
    ProcessAdultCustomer()
Else
    ' Minor customer processing
    ProcessMinorCustomer()
End If
\`\`\`

#### Complex Conditions
\`\`\`vb
' Multiple conditions with logical operators
If customerAge >= 18 AndAlso creditScore > 700 AndAlso income > 50000 Then
    ' Approved for premium services
    status = "Premium Approved"
ElseIf customerAge >= 18 AndAlso creditScore > 600 Then
    ' Approved for standard services
    status = "Standard Approved"
Else
    ' Requires manual review
    status = "Manual Review Required"
End If
\`\`\`

### Switch Activity
Use Switch for multiple distinct conditions based on a single value.

\`\`\`vb
' Switch based on customer type
Select Case customerType
    Case "Premium"
        discount = 0.15
        serviceLevel = "Priority"
    Case "Standard"
        discount = 0.05
        serviceLevel = "Regular"
    Case "Basic"
        discount = 0.0
        serviceLevel = "Standard"
    Case Else
        discount = 0.0
        serviceLevel = "Basic"
End Select
\`\`\`

## Iterative Processes (Loops)

### For Each Activity
Iterate through collections of items such as files, data rows, or lists.

#### Processing File Collections
\`\`\`vb
' Process multiple files in a directory
fileList = Directory.GetFiles("C:\Invoices", "*.pdf")

For Each filePath In fileList
    ' Extract invoice data
    invoiceData = ExtractInvoiceData(filePath)
    
    ' Process each invoice
    ProcessInvoice(invoiceData)
    
    ' Move to processed folder
    File.Move(filePath, "C:\Processed\" + Path.GetFileName(filePath))
Next
\`\`\`

#### Processing DataTable Rows
\`\`\`vb
' Process customer data from Excel
customerTable = ReadDataTable("customers.xlsx")

For Each row As DataRow In customerTable.Rows
    customerName = row("Name").ToString()
    customerEmail = row("Email").ToString()
    customerPhone = row("Phone").ToString()
    
    ' Send personalized email
    SendCustomerEmail(customerName, customerEmail)
    
    ' Update customer status
    UpdateCustomerStatus(customerName, "Processed")
Next
\`\`\`

### While Activity
Execute activities repeatedly while a condition remains true.

\`\`\`vb
' Process items until queue is empty
While GetQueueItemsCount() > 0
    ' Get next item from queue
    queueItem = GetQueueItem()
    
    If queueItem IsNot Nothing Then
        ' Process the item
        ProcessQueueItem(queueItem)
        
        ' Mark as completed
        SetTransactionStatus(queueItem, "Successful")
    End If
End While
\`\`\`

### Do While Activity
Similar to While, but executes at least once before checking the condition.

\`\`\`vb
' Retry operation until successful
Dim success As Boolean = False
Dim retryCount As Integer = 0

Do
    Try
        ' Attempt the operation
        result = PerformWebServiceCall()
        success = True
    Catch ex As Exception
        retryCount += 1
        If retryCount >= maxRetries Then
            Throw New Exception("Maximum retries exceeded")
        End If
        Delay 5000 ' Wait 5 seconds before retry
    End Try
Loop While Not success AndAlso retryCount < maxRetries
\`\`\`

## Advanced Control Flow Patterns

### Parallel Processing
Execute multiple branches simultaneously for improved performance.

\`\`\`vb
' Parallel workflow example
Parallel
    Branch 1: ProcessInvoices()
    Branch 2: ProcessPurchaseOrders()
    Branch 3: GenerateReports()
End Parallel
\`\`\`

### State Machine Workflows
Handle complex business processes with multiple states and transitions.

#### State Machine Structure
- **States**: Represent different stages of the process
- **Transitions**: Define how to move between states
- **Triggers**: Events that cause state transitions

\`\`\`vb
' Order processing state machine
States:
    - OrderReceived
    - PaymentProcessing
    - InventoryCheck
    - Shipping
    - Completed
    - Cancelled

Transitions:
    OrderReceived → PaymentProcessing (when payment initiated)
    PaymentProcessing → InventoryCheck (when payment successful)
    PaymentProcessing → Cancelled (when payment failed)
    InventoryCheck → Shipping (when items available)
    InventoryCheck → Cancelled (when items unavailable)
    Shipping → Completed (when shipped)
\`\`\`

## Error Handling Strategies

### Try-Catch Activity
The primary mechanism for handling exceptions and errors in UiPath.

#### Basic Error Handling
\`\`\`vb
Try
    ' Risky operation
    result = DivideNumbers(dividend, divisor)
    LogMessage("Calculation successful: " + result.ToString())
Catch ex As DivideByZeroException
    ' Handle specific exception type
    LogMessage("Error: Division by zero attempted")
    result = 0
Catch ex As Exception
    ' Handle any other exception
    LogMessage("Unexpected error: " + ex.Message)
    result = -1
Finally
    ' Cleanup code that always executes
    LogMessage("Calculation attempt completed")
End Try
\`\`\`

#### Advanced Exception Handling
\`\`\`vb
Try
    ' Complex operation with multiple failure points
    customerData = RetrieveCustomerData(customerId)
    validationResult = ValidateCustomerData(customerData)
    
    If validationResult.IsValid Then
        processResult = ProcessCustomer(customerData)
        UpdateDatabase(customerId, processResult)
    Else
        Throw New ValidationException("Customer data validation failed")
    End If
    
Catch ex As TimeoutException
    ' Handle timeout scenarios
    LogError("Service timeout occurred", ex)
    RetryOperation(customerId)
    
Catch ex As ValidationException
    ' Handle validation errors
    LogWarning("Validation failed: " + ex.Message)
    SendValidationErrorNotification(customerId, ex.Message)
    
Catch ex As DatabaseException
    ' Handle database errors
    LogError("Database error occurred", ex)
    QueueForManualProcessing(customerId)
    
Catch ex As Exception
    ' Handle unexpected errors
    LogError("Unexpected error in customer processing", ex)
    SendErrorNotification("Critical error in automation", ex)
    
Finally
    ' Cleanup resources
    CloseConnections()
    UpdateProcessingStatus(customerId, "Completed")
End Try
\`\`\`

### Retry Mechanisms
Implement intelligent retry logic for transient failures.

#### Simple Retry Pattern
\`\`\`vb
Dim maxRetries As Integer = 3
Dim retryCount As Integer = 0
Dim success As Boolean = False

While retryCount < maxRetries AndAlso Not success
    Try
        ' Attempt the operation
        result = CallWebService(requestData)
        success = True
        LogMessage("Operation successful on attempt " + (retryCount + 1).ToString())
        
    Catch ex As WebException
        retryCount += 1
        If retryCount < maxRetries Then
            ' Exponential backoff delay
            delayMs = 1000 * Math.Pow(2, retryCount - 1)
            LogMessage($"Attempt {retryCount} failed, retrying in {delayMs}ms")
            Delay delayMs
        Else
            LogError("All retry attempts failed", ex)
            Throw ex
        End If
    End Try
End While
\`\`\`

#### Advanced Retry with Different Strategies
\`\`\`vb
' Retry configuration
retryConfig = New Dictionary(Of String, Object) From {
    {"maxRetries", 5},
    {"baseDelay", 1000},
    {"maxDelay", 30000},
    {"backoffMultiplier", 2.0},
    {"retryableExceptions", {"TimeoutException", "WebException"}}
}

' Retry implementation
For attempt = 1 To retryConfig("maxRetries")
    Try
        result = PerformOperation()
        Exit For ' Success, exit retry loop
        
    Catch ex As Exception
        If IsRetryableException(ex, retryConfig("retryableExceptions")) AndAlso 
           attempt < retryConfig("maxRetries") Then
            
            ' Calculate delay with exponential backoff
            delay = Math.Min(
                retryConfig("baseDelay") * Math.Pow(retryConfig("backoffMultiplier"), attempt - 1),
                retryConfig("maxDelay")
            )
            
            LogMessage($"Attempt {attempt} failed: {ex.Message}. Retrying in {delay}ms")
            Delay delay
        Else
            ' Final attempt failed or non-retryable exception
            LogError($"Operation failed after {attempt} attempts", ex)
            Throw ex
        End If
    End Try
Next
\`\`\`

### Global Exception Handling
Implement application-level exception handling for unhandled errors.

#### Global Exception Handler Setup
\`\`\`vb
' In Main.xaml or application entry point
Try
    ' Main application logic
    ExecuteBusinessProcess()
    
Catch ex As BusinessException
    ' Handle business logic exceptions
    HandleBusinessException(ex)
    
Catch ex As SystemException
    ' Handle system-level exceptions
    HandleSystemException(ex)
    
Catch ex As Exception
    ' Handle any unhandled exceptions
    HandleUnexpectedException(ex)
    
Finally
    ' Cleanup and finalization
    PerformCleanup()
    SendCompletionNotification()
End Try
\`\`\`

## Best Practices for Control Flow and Error Handling

### Design Principles

#### Keep It Simple
- Use the simplest control structure that meets your needs
- Avoid deeply nested conditions when possible
- Break complex logic into smaller, manageable pieces

#### Fail Fast
- Validate inputs early in the process
- Check prerequisites before starting complex operations
- Use guard clauses to handle invalid conditions quickly

#### Graceful Degradation
- Provide alternative paths when primary operations fail
- Implement fallback mechanisms for critical processes
- Ensure partial success is better than complete failure

### Error Handling Guidelines

#### Log Everything
- Record all exceptions with sufficient detail
- Include context information (customer ID, operation type, etc.)
- Use different log levels (Error, Warning, Information)

#### User-Friendly Messages
- Provide clear, actionable error messages
- Avoid technical jargon in user-facing messages
- Include steps for resolution when possible

#### Recovery Strategies
- Implement automatic recovery for transient failures
- Provide manual intervention points for complex errors
- Maintain data consistency during error scenarios

## Testing Control Flow and Error Handling

### Test Scenarios

#### Happy Path Testing
- Verify normal flow execution
- Test all conditional branches
- Validate loop termination conditions

#### Error Path Testing
- Simulate various exception scenarios
- Test retry mechanisms with different failure patterns
- Verify cleanup code execution in error conditions

#### Edge Case Testing
- Test boundary conditions (empty lists, null values)
- Verify behavior with unexpected data types
- Test performance with large datasets

## Key Takeaways

Effective control flow and error handling are crucial for building robust, production-ready automations. Focus on creating clear, maintainable logic structures that can handle both expected and unexpected scenarios. Implement comprehensive error handling with appropriate retry mechanisms and logging. Always test your control flow logic thoroughly, including error scenarios and edge cases. Remember that good error handling can make the difference between a fragile automation and a reliable business solution.`,
        duration: '1.5 hours',
        difficulty: 'intermediate',
        practiceExercises: [
          'Build decision-based workflows',
          'Implement try-catch error handling',
          'Create retry mechanisms for unstable processes'
        ]
      }
    ]
  },
  {
    id: 'uipath-advanced-development',
    title: 'Advanced UiPath Automation Development',
    description: 'Develop complex automation solutions with advanced UiPath features, custom activities, and enterprise patterns.',
    level: 'advanced',
    duration: '15 hours',
    category: 'uipath',
    platform: 'UiPath Studio',
    certificationPrep: true,
    prerequisites: ['uipath-fundamentals'],
    learningObjectives: [
      'Design scalable and maintainable automation architectures',
      'Implement advanced data processing and API integrations',
      'Create custom activities and reusable components',
      'Optimize performance and implement monitoring',
      'Apply enterprise automation patterns and best practices'
    ],
    tags: ['UiPath', 'Advanced Development', 'APIs', 'Custom Activities', 'Performance'],
    modules: [
      {
        id: 'advanced-data-processing',
        title: 'Advanced Data Processing and Excel Automation',
        description: 'Master complex data manipulation, Excel automation, and database operations',
        content: `# Advanced Data Processing and Excel Automation

## Working with Large Datasets

### Memory Management Strategies
When processing large datasets, efficient memory management is crucial for performance and stability.

#### Streaming Data Processing
\`\`\`vb
' Process large files line by line instead of loading everything into memory
Using reader As New StreamReader("large-file.csv")
    Dim header As String = reader.ReadLine()
    
    While Not reader.EndOfStream
        Dim line As String = reader.ReadLine()
        Dim values() As String = line.Split(","c)
        
        ' Process each row immediately
        ProcessDataRow(values)
        
        ' Optional: Clear variables to free memory
        values = Nothing
    End While
End Using
\`\`\`

#### Batch Processing
\`\`\`vb
' Process data in smaller batches
Dim batchSize As Integer = 1000
Dim totalRows As Integer = dataTable.Rows.Count
Dim currentBatch As Integer = 0

While currentBatch * batchSize < totalRows
    Dim startIndex As Integer = currentBatch * batchSize
    Dim endIndex As Integer = Math.Min(startIndex + batchSize - 1, totalRows - 1)
    
    ' Process batch
    For i = startIndex To endIndex
        ProcessDataRow(dataTable.Rows(i))
    Next
    
    ' Optional: Force garbage collection
    GC.Collect()
    currentBatch += 1
End While
\`\`\`

## Advanced Excel Automation

### Excel Application Scope vs. Excel Activities

#### Modern Excel Activities (Recommended)
- Better performance and reliability
- No Excel installation required
- Support for .xlsx, .xls, and .csv formats
- Memory efficient for large files

\`\`\`vb
' Read Excel without opening Excel application
dataTable = ReadRange("data.xlsx", "Sheet1", "A1:Z1000")

' Write data efficiently
WriteRange("output.xlsx", dataTable, "Sheet1", "A1")
\`\`\`

#### Excel Application Scope (Legacy)
Use only when modern activities don't support required functionality:

\`\`\`vb
Excel Application Scope "workbook.xlsx"
    ' Use Excel-specific features
    workbook.SaveAs("new-format.xlsm", FileFormat.xlOpenXMLWorkbookMacroEnabled)
    
    ' Access Excel object model
    worksheet.Range("A1:C10").Sort(worksheet.Range("A1"))
End Excel Application Scope
\`\`\`

### Advanced Excel Operations

#### Dynamic Range Detection
\`\`\`vb
' Find the actual used range in a worksheet
Dim lastRow As Integer = GetLastDataRow("data.xlsx", "Sheet1", "A")
Dim lastColumn As String = GetLastDataColumn("data.xlsx", "Sheet1", 1)
Dim dynamicRange As String = "A1:" + lastColumn + lastRow.ToString()

' Read only the data that exists
dataTable = ReadRange("data.xlsx", "Sheet1", dynamicRange)
\`\`\`

#### Excel Formula Integration
\`\`\`vb
' Write formulas to Excel cells
formulaValue = "=SUM(B2:B" + lastRow.ToString() + ")"
WriteCell("report.xlsx", "B" + (lastRow + 1).ToString(), formulaValue, "Sheet1")

' Read calculated formula results
calculatedValue = ReadCell("report.xlsx", "B" + (lastRow + 1).ToString(), "Sheet1")
\`\`\`

#### Advanced Data Formatting
\`\`\`vb
' Format cells programmatically
Excel Application Scope "report.xlsx"
    ' Set number format
    worksheet.Range("B:B").NumberFormat = "$#,##0.00"
    
    ' Apply conditional formatting
    Dim condition = worksheet.Range("C:C").FormatConditions.Add(
        Type:=XlFormatConditionType.xlCellValue,
        Operator:=XlFormatConditionOperator.xlGreater,
        Formula1:="1000"
    )
    condition.Interior.Color = RGB(144, 238, 144) ' Light green
    
    ' Create charts
    Dim chart = worksheet.Shapes.AddChart2(
        Style:=240,
        XlChartType:=xlColumnClustered,
        Left:=300,
        Top:=50,
        Width:=400,
        Height:=300
    ).Chart
    chart.SetSourceData(worksheet.Range("A1:B10"))
End Excel Application Scope
\`\`\`

### Excel Data Validation and Quality

#### Data Validation Techniques
\`\`\`vb
' Validate Excel data before processing
For Each row As DataRow In excelData.Rows
    ' Check for required fields
    If String.IsNullOrEmpty(row("CustomerID").ToString()) Then
        LogError("Missing CustomerID in row " + excelData.Rows.IndexOf(row).ToString())
        Continue For
    End If
    
    ' Validate data types
    Dim amount As Double
    If Not Double.TryParse(row("Amount").ToString(), amount) Then
        LogError("Invalid amount format in row " + excelData.Rows.IndexOf(row).ToString())
        Continue For
    End If
    
    ' Validate business rules
    If amount < 0 Then
        LogError("Negative amount not allowed in row " + excelData.Rows.IndexOf(row).ToString())
        Continue For
    End If
    
    ' Process valid row
    ProcessValidRow(row)
Next
\`\`\`

#### Data Cleaning and Transformation
\`\`\`vb
' Clean and standardize Excel data
For Each row As DataRow In excelData.Rows
    ' Trim whitespace
    row("CustomerName") = row("CustomerName").ToString().Trim()
    
    ' Standardize phone numbers
    Dim phone As String = row("Phone").ToString()
    phone = Regex.Replace(phone, "[^0-9]", "") ' Remove non-numeric characters
    If phone.Length = 10 Then
        phone = phone.Insert(3, "-").Insert(7, "-")
        row("Phone") = phone
    End If
    
    ' Standardize dates
    Dim dateValue As DateTime
    If DateTime.TryParse(row("OrderDate").ToString(), dateValue) Then
        row("OrderDate") = dateValue.ToString("yyyy-MM-dd")
    End If
Next
\`\`\`

## Database Connectivity and Operations

### Connection Management

#### SQL Server Connection
\`\`\`vb
' Using Connect activity for SQL Server
connectionString = "Server=localhost;Database=CustomerDB;Integrated Security=true;"

Connect To Database connectionString
    ' Execute database operations here
    customerData = ExecuteQuery("SELECT * FROM Customers WHERE Status = 'Active'")
    
    ' Process results
    For Each row As DataRow In customerData.Rows
        ProcessCustomer(row)
    Next
End Connect To Database
\`\`\`

#### Oracle Database Connection
\`\`\`vb
' Oracle connection with specific provider
connectionString = "Data Source=localhost:1521/XE;User Id=hr;Password=password;"
providerName = "Oracle.ManagedDataAccess.Client"

Connect To Database connectionString, providerName
    ' Oracle-specific operations
    oracleData = ExecuteQuery("SELECT * FROM EMPLOYEES WHERE DEPARTMENT_ID = :deptId", 
                              Parameters:=New Dictionary(Of String, Object) From {{"deptId", 10}})
End Connect To Database
\`\`\`

### Advanced Database Operations

#### Parameterized Queries
Always use parameterized queries to prevent SQL injection:

\`\`\`vb
' Safe parameterized query
parameters = New Dictionary(Of String, Object) From {
    {"customerName", customerNameVariable},
    {"startDate", startDateVariable},
    {"endDate", endDateVariable}
}

query = "SELECT * FROM Orders WHERE CustomerName = @customerName AND OrderDate BETWEEN @startDate AND @endDate"
results = ExecuteQuery(query, Parameters:=parameters)
\`\`\`

#### Bulk Operations
\`\`\`vb
' Bulk insert for large datasets
bulkInsertQuery = "INSERT INTO Orders (CustomerID, OrderDate, Amount) VALUES (@CustomerID, @OrderDate, @Amount)"

' Prepare data for bulk insert
bulkData = New List(Of Dictionary(Of String, Object))
For Each row As DataRow In processedData.Rows
    bulkData.Add(New Dictionary(Of String, Object) From {
        {"CustomerID", row("CustomerID")},
        {"OrderDate", row("OrderDate")},
        {"Amount", row("Amount")}
    })
Next

' Execute bulk insert
ExecuteNonQueryBulk(bulkInsertQuery, bulkData)
\`\`\`

#### Transaction Management
\`\`\`vb
' Use transactions for data integrity
Begin Transaction
Try
    ' Multiple related operations
    ExecuteNonQuery("UPDATE Inventory SET Quantity = Quantity - @qty WHERE ProductID = @id", 
                    Parameters:=New Dictionary(Of String, Object) From {{"qty", orderQty}, {"id", productId}})
    
    ExecuteNonQuery("INSERT INTO Orders (ProductID, Quantity, CustomerID) VALUES (@pid, @qty, @cid)", 
                    Parameters:=New Dictionary(Of String, Object) From {{"pid", productId}, {"qty", orderQty}, {"cid", customerId}})
    
    ' Commit if all operations succeed
    Commit Transaction
    LogMessage("Order processed successfully")
    
Catch ex As Exception
    ' Rollback on any error
    Rollback Transaction
    LogError("Order processing failed: " + ex.Message)
    Throw ex
End Try
\`\`\`

### Data Integration Patterns

#### ETL (Extract, Transform, Load) Implementation
\`\`\`vb
' Extract data from multiple sources
sourceData1 = ReadRange("sales-data.xlsx", "Sheet1")
sourceData2 = ExecuteQuery("SELECT * FROM CustomerMaster")
sourceData3 = CallWebService("https://api.external.com/products")

' Transform data
mergedData = New DataTable()
' Define schema
mergedData.Columns.Add("CustomerID", GetType(String))
mergedData.Columns.Add("CustomerName", GetType(String))
mergedData.Columns.Add("SalesAmount", GetType(Double))
mergedData.Columns.Add("ProductCategory", GetType(String))

' Merge and transform data
For Each salesRow As DataRow In sourceData1.Rows
    customerID = salesRow("CustomerID").ToString()
    
    ' Lookup customer info
    customerInfo = sourceData2.AsEnumerable().FirstOrDefault(
        Function(row) row("ID").ToString() = customerID)
    
    ' Lookup product info
    productInfo = sourceData3.FirstOrDefault(
        Function(p) p("id").ToString() = salesRow("ProductID").ToString())
    
    If customerInfo IsNot Nothing AndAlso productInfo IsNot Nothing Then
        newRow = mergedData.NewRow()
        newRow("CustomerID") = customerID
        newRow("CustomerName") = customerInfo("Name")
        newRow("SalesAmount") = Convert.ToDouble(salesRow("Amount"))
        newRow("ProductCategory") = productInfo("category")
        mergedData.Rows.Add(newRow)
    End If
Next

' Load transformed data
BulkInsertData("AnalyticsDB", "SalesAnalytics", mergedData)
\`\`\`

## Performance Optimization Techniques

### Efficient Data Processing

#### Use DataView for Filtering
\`\`\`vb
' More efficient than looping through DataTable
dataView = New DataView(largeDataTable)
dataView.RowFilter = "Amount > 1000 AND Status = 'Active'"
dataView.Sort = "Amount DESC"

For Each rowView As DataRowView In dataView
    ProcessHighValueCustomer(rowView.Row)
Next
\`\`\`

#### Optimize LINQ Queries
\`\`\`vb
' Efficient LINQ usage
highValueCustomers = dataTable.AsEnumerable().
    Where(Function(row) Convert.ToDouble(row("Amount")) > 1000).
    OrderByDescending(Function(row) Convert.ToDouble(row("Amount"))).
    Take(100).
    ToList()
\`\`\`

### Memory Management
\`\`\`vb
' Dispose large objects explicitly
Using dataReader As IDataReader = ExecuteReader(query)
    While dataReader.Read()
        ProcessRecord(dataReader)
    End While
End Using ' Automatically disposed

' Clear large collections
largeDataTable.Clear()
largeDataTable.Dispose()
largeDataTable = Nothing
\`\`\`

## Error Handling in Data Operations

### Database Error Handling
\`\`\`vb
Try
    Connect To Database connectionString
        result = ExecuteQuery(query, parameters)
    End Connect To Database
    
Catch ex As SqlException
    Select Case ex.Number
        Case 2 ' Connection timeout
            LogError("Database connection timeout")
            ' Implement retry logic
        Case 18456 ' Login failed
            LogError("Database authentication failed")
            ' Handle authentication error
        Case 547 ' Foreign key constraint
            LogError("Data integrity violation")
            ' Handle constraint violation
        Case Else
            LogError("SQL Error: " + ex.Message)
    End Select
    
Catch ex As Exception
    LogError("Unexpected database error: " + ex.Message)
End Try
\`\`\`

### Excel Error Handling
\`\`\`vb
Try
    excelData = ReadRange("data.xlsx", "Sheet1")
    
Catch ex As FileNotFoundException
    LogError("Excel file not found: " + ex.FileName)
    ' Handle missing file
    
Catch ex As UnauthorizedAccessException
    LogError("Cannot access Excel file - file may be open in another application")
    ' Handle file access issues
    
Catch ex As Exception
    LogError("Excel processing error: " + ex.Message)
End Try
\`\`\`

## Key Takeaways

Advanced data processing requires careful consideration of performance, memory management, and error handling. Use modern Excel activities when possible, implement proper parameterized queries for database operations, and always validate data before processing. Focus on efficient algorithms for large datasets and implement comprehensive error handling for robust automation solutions. Remember that good data processing forms the backbone of reliable business automation.`,
        duration: '3 hours',
        difficulty: 'advanced',
        practiceExercises: [
          'Process large Excel files with optimization',
          'Connect to databases and execute SQL queries',
          'Implement data validation and transformation'
        ]
      },
      {
        id: 'api-integration',
        title: 'API Integration and Web Services',
        description: 'Integrate with REST APIs, handle authentication, and process JSON/XML data',
        content: 'Comprehensive API integration including authentication, data parsing, and error handling.',
        duration: '3 hours',
        difficulty: 'advanced',
        practiceExercises: [
          'Consume REST APIs with authentication',
          'Parse and manipulate JSON responses',
          'Handle API rate limits and errors'
        ]
      },
      {
        id: 'custom-activities',
        title: 'Custom Activities and Libraries',
        description: 'Create reusable custom activities and build activity libraries',
        content: 'Developing custom activities, creating activity libraries, and sharing components across projects.',
        duration: '4 hours',
        difficulty: 'advanced',
        practiceExercises: [
          'Create a custom activity using C#',
          'Build and publish an activity library',
          'Implement custom validators and designers'
        ]
      },
      {
        id: 'advanced-patterns',
        title: 'Enterprise Automation Patterns',
        description: 'Implement enterprise patterns like REFramework and queue processing',
        content: 'Advanced automation patterns including REFramework, queue-based processing, and state machines.',
        duration: '3 hours',
        difficulty: 'advanced',
        practiceExercises: [
          'Implement REFramework pattern',
          'Design queue-based processing system',
          'Create state machine workflows'
        ]
      },
      {
        id: 'performance-monitoring',
        title: 'Performance Optimization and Monitoring',
        description: 'Optimize automation performance and implement comprehensive monitoring',
        content: 'Performance tuning, memory management, and implementing monitoring and analytics.',
        duration: '2 hours',
        difficulty: 'advanced',
        practiceExercises: [
          'Profile and optimize slow automations',
          'Implement performance logging',
          'Create monitoring dashboards'
        ]
      }
    ]
  },
  {
    id: 'uipath-orchestrator',
    title: 'UiPath Orchestrator & Enterprise Management',
    description: 'Master UiPath Orchestrator for enterprise automation management, scheduling, monitoring, and governance.',
    level: 'intermediate',
    duration: '12 hours',
    category: 'uipath',
    platform: 'UiPath Orchestrator',
    certificationPrep: true,
    prerequisites: ['uipath-fundamentals'],
    learningObjectives: [
      'Configure and manage UiPath Orchestrator environments',
      'Implement robot management and scheduling strategies',
      'Set up monitoring, logging, and alerting systems',
      'Establish governance and security policies',
      'Manage queues, assets, and credentials securely'
    ],
    tags: ['UiPath', 'Orchestrator', 'Enterprise', 'Management', 'Governance'],
    modules: [
      {
        id: 'orchestrator-setup',
        title: 'Orchestrator Installation and Configuration',
        description: 'Install, configure, and secure UiPath Orchestrator for enterprise use',
        content: 'Complete guide to Orchestrator installation, configuration, and initial setup for enterprise environments.',
        duration: '2.5 hours',
        difficulty: 'intermediate'
      },
      {
        id: 'robot-management',
        title: 'Robot Management and Deployment',
        description: 'Manage robot fleets, deployments, and environment configurations',
        content: 'Comprehensive robot lifecycle management, deployment strategies, and environment configuration.',
        duration: '3 hours',
        difficulty: 'intermediate'
      },
      {
        id: 'scheduling-queues',
        title: 'Process Scheduling and Queue Management',
        description: 'Implement advanced scheduling and manage work queues efficiently',
        content: 'Advanced scheduling strategies, queue-based processing, and workload distribution.',
        duration: '2.5 hours',
        difficulty: 'intermediate'
      },
      {
        id: 'monitoring-analytics',
        title: 'Monitoring, Logging, and Analytics',
        description: 'Set up comprehensive monitoring and generate actionable insights',
        content: 'Monitoring strategies, log analysis, performance metrics, and business intelligence.',
        duration: '2 hours',
        difficulty: 'intermediate'
      },
      {
        id: 'security-governance',
        title: 'Security and Governance Framework',
        description: 'Implement security policies, access controls, and governance frameworks',
        content: 'Enterprise security implementation, role-based access control, and governance best practices.',
        duration: '2 hours',
        difficulty: 'advanced'
      }
    ]
  },
  {
    id: 'uipath-ai-document',
    title: 'UiPath AI & Document Understanding',
    description: 'Leverage UiPath AI capabilities for intelligent document processing, machine learning, and cognitive automation.',
    level: 'advanced',
    duration: '14 hours',
    category: 'uipath',
    platform: 'UiPath AI Center & Document Understanding',
    certificationPrep: false,
    prerequisites: ['uipath-fundamentals', 'uipath-advanced-development'],
    learningObjectives: [
      'Implement UiPath Document Understanding for invoice and form processing',
      'Deploy and consume ML models through UiPath AI Center',
      'Build cognitive automation solutions with Computer Vision',
      'Integrate NLP and text analytics in automation workflows',
      'Develop intelligent process automation with AI insights'
    ],
    tags: ['UiPath', 'AI', 'Document Understanding', 'Machine Learning', 'Computer Vision'],
    modules: [
      {
        id: 'document-understanding',
        title: 'Document Understanding Framework',
        description: 'Master intelligent document processing with UiPath Document Understanding',
        content: 'Complete guide to Document Understanding including classification, extraction, and validation.',
        duration: '4 hours',
        difficulty: 'advanced'
      },
      {
        id: 'ai-center',
        title: 'UiPath AI Center and ML Model Integration',
        description: 'Deploy and consume machine learning models through AI Center',
        content: 'AI Center setup, model deployment, and integration with automation workflows.',
        duration: '3.5 hours',
        difficulty: 'advanced'
      },
      {
        id: 'computer-vision',
        title: 'Computer Vision and Image Processing',
        description: 'Implement computer vision solutions for visual automation tasks',
        content: 'Computer vision techniques, image processing, and visual element recognition.',
        duration: '3 hours',
        difficulty: 'advanced'
      },
      {
        id: 'nlp-text-analytics',
        title: 'Natural Language Processing and Text Analytics',
        description: 'Apply NLP techniques for text processing and sentiment analysis',
        content: 'NLP implementation, text analytics, and language understanding in automation.',
        duration: '2.5 hours',
        difficulty: 'advanced'
      },
      {
        id: 'intelligent-automation',
        title: 'Building Intelligent Automation Solutions',
        description: 'Combine AI capabilities to create cognitive automation solutions',
        content: 'Integrating multiple AI technologies to build intelligent, decision-making automation.',
        duration: '1 hour',
        difficulty: 'advanced'
      }
    ]
  },
  {
    id: 'uipath-testing-deployment',
    title: 'UiPath Testing & Deployment Best Practices',
    description: 'Master testing strategies, CI/CD pipelines, and deployment best practices for enterprise UiPath automation.',
    level: 'intermediate',
    duration: '10 hours',
    category: 'uipath',
    platform: 'UiPath Test Suite & Studio',
    certificationPrep: false,
    prerequisites: ['uipath-fundamentals'],
    learningObjectives: [
      'Design comprehensive testing strategies for RPA projects',
      'Implement automated testing with UiPath Test Suite',
      'Set up CI/CD pipelines for automation deployment',
      'Establish quality assurance and code review processes',
      'Manage version control and release management'
    ],
    tags: ['UiPath', 'Testing', 'CI/CD', 'Quality Assurance', 'Deployment'],
    modules: [
      {
        id: 'testing-strategies',
        title: 'RPA Testing Strategies and Methodologies',
        description: 'Design comprehensive testing approaches for RPA projects',
        content: 'Testing methodologies, test case design, and quality assurance strategies for RPA.',
        duration: '2.5 hours',
        difficulty: 'intermediate'
      },
      {
        id: 'test-suite',
        title: 'UiPath Test Suite Implementation',
        description: 'Implement automated testing using UiPath Test Suite',
        content: 'Test Suite setup, test automation, and test management processes.',
        duration: '3 hours',
        difficulty: 'intermediate'
      },
      {
        id: 'cicd-pipelines',
        title: 'CI/CD Pipelines for RPA',
        description: 'Set up continuous integration and deployment for automation projects',
        content: 'CI/CD implementation, automated deployment, and release management for RPA.',
        duration: '2.5 hours',
        difficulty: 'intermediate'
      },
      {
        id: 'version-control',
        title: 'Version Control and Code Management',
        description: 'Implement version control best practices for UiPath projects',
        content: 'Git integration, branching strategies, and code management for team development.',
        duration: '1.5 hours',
        difficulty: 'intermediate'
      },
      {
        id: 'quality-assurance',
        title: 'Quality Assurance and Code Review',
        description: 'Establish QA processes and code review standards',
        content: 'Quality assurance frameworks, code review processes, and maintenance strategies.',
        duration: '0.5 hours',
        difficulty: 'intermediate'
      }
    ]
  }
];